import { Autocomplete, AutocompleteChangeReason, AutocompleteCloseReason, Box, Chip, Grid, Stack, TextField, Tooltip } from "@mui/material";
import { FC, useMemo, useState } from "react";
import UndoIcon from '@mui/icons-material/Undo';
import { useDispatch } from "react-redux";
import { crudGetList } from "ra-core";
import { useFileCataAmbientDataContext } from "./FileCataAmbientDataProvider";
import { useFileCataInfoEditContext } from "./FileCataInfoEditContext";
import { SimpleOption } from "../governance/search/AmbientFacilitiesContext";
import { FileTagResponseDto } from "~/gen/api/securefile";
import Create from "~/controller/Create";
import WithSave from "~/controller/WithSave";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG } from "~/constants";
import ReadonlyField from "../input/ReadonlyField";


const FileCataInfoEdit: FC = () => {
  const hasClssPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const hasEditPermit = useHasPermit([MANAGE_DATA_CATALOG])
  const { classifications, tags } = useFileCataAmbientDataContext()
  const {
    states: { tagsToAddSet, tagsToDelSet },
    getEffectClssItem,
    getDisplayTags,
    setClssItem,
    addTag,
    delTag,
  } = useFileCataInfoEditContext()

  const [states, setStates] = useState({
    tagSearch: '',
    tagWarning: '',
  })
  const dispatch = useDispatch()

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, key: -1 } as SimpleOption,
      ...classifications
    ]
  }, [classifications])
  
  const onTagSelectionChange = (tagCreate: (sValue:any,callbacks?:any)=>void) => (event:any, value: any, reason:AutocompleteChangeReason, detail: any) => {
    if (!hasEditPermit) { return }
    if (reason === 'selectOption') {  
      const newOption = value as FileTagResponseDto
      addTag(newOption)
      setStates(v => ({...v, tagSearch: '', }))
    } else if (reason === 'createOption') {
      const sValue = value as string
      if (sValue.length > 100) {
        setStates(v => ({...v, tagWarning: 'exceeded 100 characters' }))
      }
      tagCreate({name: sValue}, {
        onSuccess: ({data}:{data:FileTagResponseDto}) => {
          const tagx = data as any
          tagx.id = data.tagId
          tagx.text = data.name
          addTag(tagx)
          setStates(v => ({...v, tagSearch: '', }))
          dispatch(crudGetList('secure-files-tags', pages(), sorts(), {}))
        },
      })
    }
  }
  const handleTagInputBlur = (event:any, reason:AutocompleteCloseReason) => {
    if ((reason === 'blur' || reason === 'escape') && event.target.value) {
      setStates(v => ({...v, tagWarning: 'Type "Enter" key to finish Tag creation' }))
    }
  }
  return (
    <>
      <Grid container sx={{ py: 2, px: 4, }} >
        <Grid item xs={4} container>
          <Stack direction="column" spacing={2} sx={{ width: '100%'}}>
            {hasClssPermit
            
            ? <Autocomplete 
                disableClearable={true}
                options={classificationSelectOptions}
                getOptionLabel={(option) => (option.text || option) as string}
                value={getEffectClssItem() as SimpleOption}

                renderInput={(params) => 
                  <TextField label="Classification" {...params} />
                } 
                onChange={(event, value, reason) => {
                  if (reason === 'selectOption') {
                    if ((value as any)?.value === null) { // choosed '[None]'
                      setClssItem('')
                    } else {
                      const cls1 = value as SecondaryClassificationResponseDto
                      setClssItem(cls1)
                    }
                  }
                }}
              />
            : <ReadonlyField label="Classification" value={(getEffectClssItem() as any).text}/>}
            {hasEditPermit &&
              <Create resource="secure-files-tags" basePath="">
                <WithSave 
                  render={({save}) => 
                    <Autocomplete 
                      freeSolo={true}
                      clearOnBlur={true}
                      options={tags}
                      inputValue={states.tagSearch}
                      getOptionLabel={(x) => (x.text || x) as string}
                      renderInput={(params) =>                  
                        <TextField 
                          placeholder="Add Tag..." 
                          {...params} 
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': "add tag" 
                          }}
                          fullWidth 
                          error={!!states.tagWarning} 
                          helperText={states.tagWarning} />
                      }
      
                      onChange={onTagSelectionChange(save)}
                      onInputChange={(event:any, value:string) => { setStates(v => ({...v, tagSearch: value, tagWarning: value.length > 100 ? 'exceeded 100 characters' : '' })) }}
                      onClose={handleTagInputBlur}
                    />
                  }
                />
              </Create>}
          </Stack>
        </Grid>
        <Grid item xs={8} sx={{ 
          pl: 6,
          pr: 2,
          display: 'flex',
          flexDirection: 'column-reverse'
        }}>
          <Stack direction="row" flexWrap="wrap" 
            sx={{ 
              mt: 'auto',
              '& .MuiChip-root': { mr: 2, mb: 2, maxWidth: 400 }
            }}
          >
            {getDisplayTags().map(x =>
              <Chip key={x?.tagId} variant="outlined" 
                label={x?.name} 
                onDelete={hasEditPermit ? (() => {
                  if (tagsToDelSet.has(x?.tagId!)) {
                    addTag(x!)
                  } else {
                    delTag(x!)
                  }
                }) : undefined}
                deleteIcon={hasEditPermit && tagsToDelSet.has(x?.tagId!) 
                  ? 
                    <Tooltip title="Undo">
                      <UndoIcon fontSize="small" /> 
                    </Tooltip>
                  : undefined}
                color={
                    tagsToDelSet.has(x?.tagId!) ? "error" 
                  : tagsToAddSet.has(x?.tagId!) ? "success" 
                  : undefined
                }
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </>
  )
}

export default FileCataInfoEdit;
